import "katex/dist/katex.min.css";
import {
  Children,
  isValidElement,
  memo,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import type { PluggableList } from "unified";
import { cn } from "~/lib/utils";
import { CodeBlock } from "./CodeBlock";
import { ArtifactRef } from "./artifacts/ArtifactRef";
import { remarkArtifactRefs } from "./remarkArtifactRefs";

interface MarkdownProps {
  text: string;
}

const remarkPlugins: PluggableList = [remarkGfm, remarkMath, remarkArtifactRefs];

// `errorColor: "inherit"` keeps math that fails to parse looking like the
// surrounding prose instead of KaTeX's default red, which matters because
// formulas arrive a token at a time and are briefly incomplete. `strict:
// false` suppresses warnings on the Unicode the model likes to mix in.
const rehypePlugins: PluggableList = [[rehypeKatex, { errorColor: "inherit", strict: false }]];

// Lightweight markdown renderer scoped to assistant message bubbles. Pulls
// in nothing from the v1 assistant pipeline; just `react-markdown` + GFM,
// KaTeX math, plus our `[[name]]` -> <ArtifactRef/> plugin.
export const Markdown = memo(function Markdown({ text }: MarkdownProps) {
  return (
    <div className="text-sm leading-6">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});

// Pull the raw source and `language-*` hint out of a fenced block's inner
// <code> element so it can be re-rendered through Prism.
function extractFencedCode(children: ReactNode): { code: string; language: string } {
  const [first] = Children.toArray(children);
  if (!isValidElement<{ className?: string; children?: ReactNode }>(first)) {
    return { code: "", language: "" };
  }
  const text = Children.toArray(first.props.children)
    .filter((child): child is string => typeof child === "string")
    .join("");
  const language = /language-(\w+)/.exec(first.props.className ?? "")?.[1] ?? "";
  return { code: text.replace(/\n$/, ""), language };
}

// react-markdown passes the hast node alongside the DOM props; spreading it
// onto an element renders a literal node="[object Object]" attribute.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function dropNode<P extends { node?: unknown }>({ node, ...props }: P): Omit<P, "node"> {
  return props;
}

// A tag override that is just classes, plus any fixed attributes.
function styled<Tag extends keyof React.JSX.IntrinsicElements>(
  tag: Tag,
  classes: string,
  fixedProps?: ComponentPropsWithoutRef<Tag>
) {
  return function Styled({
    className,
    ...props
  }: ComponentPropsWithoutRef<Tag> & { node?: unknown }) {
    const Component = tag as ElementType;
    return <Component {...fixedProps} {...dropNode(props)} className={cn(classes, className)} />;
  };
}

// `artifact-ref` is emitted by remarkArtifactRefs; react-markdown's
// `components` map keys aren't typed for custom elements, so we cast.
const components = {
  "artifact-ref": ({ name }: { name: string }) => <ArtifactRef name={name} />,
  p: styled("p", "my-3 first:mt-0 last:mb-0"),
  a: styled("a", "font-medium text-primary underline underline-offset-4", {
    target: "_blank",
    rel: "noreferrer",
  }),
  ul: styled("ul", "my-3 ml-5 list-disc first:mt-0 last:mb-0 [&>li]:mt-1"),
  ol: styled("ol", "my-3 ml-5 list-decimal first:mt-0 last:mb-0 [&>li]:mt-1"),
  blockquote: styled(
    "blockquote",
    "my-3 border-l-2 border-border pl-3 italic first:mt-0 last:mb-0"
  ),
  hr: styled("hr", "my-4 border-border"),
  h1: styled("h1", "mt-4 mb-2 text-base font-semibold first:mt-0"),
  h2: styled("h2", "mt-4 mb-2 text-base font-semibold first:mt-0"),
  h3: styled("h3", "mt-3 mb-1 text-sm font-semibold first:mt-0"),
  // Inline code only. Fenced blocks are handled by `pre` below, which routes
  // through CodeBlock; className is preserved either way so `pre` can read the
  // `language-*` hint off the inner code element.
  code: ({ className, children, ...props }) => {
    const isBlock = typeof className === "string" && className.includes("language-");
    return (
      <code
        className={cn(
          !isBlock &&
            "rounded border border-border bg-background/60 px-1 py-0.5 font-mono text-[0.875em]",
          className
        )}
        {...dropNode(props)}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    const { code, language } = extractFencedCode(children);
    return <CodeBlock code={code} language={language} />;
  },
  table: ({ className, ...props }) => (
    <div className="my-3 overflow-x-auto first:mt-0 last:mb-0">
      <table
        className={cn("w-full border-collapse text-left text-[0.95em]", className)}
        {...dropNode(props)}
      />
    </div>
  ),
  th: styled("th", "whitespace-nowrap border-b border-border px-2 py-1 font-semibold"),
  td: styled("td", "break-normal border-b border-border/60 px-2 py-1"),
} as Parameters<typeof ReactMarkdown>[0]["components"];
